import stripAnsi from 'strip-ansi';
import { errorHandler } from './handler';
import { isExpoSDKUpgradeError, normalize } from './util';
import { workbenchStore } from '~/lib/stores/workbench';
import type { PotentialProblem } from '~/types/chat';
import { hashText } from '~/utils/hash';

export function registerShellHandler() {
  workbenchStore.addTerminalOutputListener(async (output) => {
    const text = stripAnsi(output);

    if (isBuildUpdate(text)) {
      errorHandler.clear((problem) => problem.type === 'terminal' || problem.type === 'shell');
    }

    if (isLikelyError(text) && !isExpoSDKUpgradeError(text)) {
      errorHandler.push({
        type: 'terminal',
        id: `terminal:${await hashMessage(text)}`,
        output: simplifyNetlifyOutput(output),
      } as PotentialProblem);
    }
  });

  workbenchStore.addShellActionListener(async (action) => {
    if (action.status.get() === 'failed') {
      const id = `${action.messageId}:${action.id}`;

      errorHandler.push({
        type: 'shell',
        id,
        exitCode: action.exitCode,
        command: action.command,
        output: action.output,
      } as PotentialProblem);
    }
  });
}

const errorPattern = new RegExp(
  [
    // vite
    /(\d+:\d+:\d+\s+((AM|PM)\s+)?)?\[vite\] ((.*)error:|failed|circular)/,

    // esbuild
    /^>\s+[\w\/.]+:\d+:\d+: error:|^>\s+error:/,

    // webpack
    /^ERROR in \./,

    // rollup (and generic SomethingError:)
    /(\[!\]\s+)?\w*Error:\s+./,

    // parcel
    /^🚨 Build failed\b/,

    // netlify build endpoint
    /^"build\.command"\sfailed/,

    // ts
    /^(?:error TS\d+:|Cannot find module|Property '.*' does not exist)/,

    // jest
    /^● .*(?:failed|error)|FAIL\s+.*test|Test suite failed to run/,

    // eslint
    /^\d+:\d+\s+error\s+/,

    // next
    /^Error:\s+Failed to compile|^\[Build error\](?:\s+Failed to compile)?/,

    // babel
    /^\[BABEL\]\s+.*Error:/,

    // expo
    /\(NO BRIDGE\) ERROR/,

    // misc
    /⨯ Error:|Require stack:|([✗⨯] )?\[ERROR\]|Unable to resolve|error during build|UnhandledPromiseRejectionWarning:/,
  ]
    .map((pattern) => pattern.source)
    .join('|'),
  'iu',
);

function isLikelyError(_text: string) {
  const text = _text.trimStart();

  // skip expensive checks for short messages that probably aren't errors
  if (text.length < 10) {
    return false;
  }

  if (text.includes('The build was canceled')) {
    return false;
  }

  return errorPattern.test(text);
}

async function hashMessage(argText: string): Promise<string> {
  // remove hash-busting elements
  const text = normalize(argText);

  return hashText(text);
}

const successPattern = new RegExp(
  [
    // vite
    /(\d+:\d+:\d+\s+((AM|PM)\s+)?)?\[vite\] hmr update/,
    /VITE v\d+\.\d+\.\d+\s+ready\sin\s\d+\sms/,
    /\[vite\] page reload/,

    // webpack
    /\[webpack-dev-server\] App updated\./,
  ]
    .map((pattern) => pattern.source)
    .join('|'),
  'iu',
);

function isBuildUpdate(text: string) {
  return successPattern.test(text.trimStart());
}

const netlifyStartOfRelevantOutput = /build\.command\sfrom\snetlify\.toml/;

function simplifyNetlifyOutput(output: string) {
  const match = output.match(netlifyStartOfRelevantOutput);

  if (!match) {
    return output;
  }

  return output.slice(match.index);
}
